import React, { useState } from "react";
import styles from "../../../../assets/styles/components/Suggestion.module.scss";
import { ReactComponent as TrashIcon } from "../../../../assets/icons/icon-trash.svg";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------
export type LocationTargetingSuggestion = {
    campaign_id: string;
    campaign_name: string;
    current_locations: { id: string; key?: string; label?: string; name: string; type: "TARGET" | "EXCLUDE" }[];
    suggested_locations: { id: string; key?: string; label?: string; name: string; type: "TARGET" | "EXCLUDE" }[];
    reason?: string;
};

// Represents a single form entry for dynamic location management
export type LocationEntry = {
    id: string; // e.g. "loc_tokyo"
    key?: string;
    label?: string;
    name: string; // e.g. "東京都"
    type: "TARGET" | "EXCLUDE";
};

type Props = {
    locations: LocationTargetingSuggestion[];
    formData: Record<string, LocationEntry[]>; // campaign_id -> array of LocationEntry
    onChange: (id: string, newLocations: LocationEntry[]) => void;
};

// -----------------------------------------------------------------------------
// Component: LocationTargetingForm
// -----------------------------------------------------------------------------
const LocationTargetingForm: React.FC<Props> = ({ locations, formData, onChange }) => {
    // Local state for the search input per campaign: { [campaign_id]: "search text" }
    const [searchQuery, setSearchQuery] = useState<Record<string, string>>({});
    const getLocationLabel = (loc: { label?: string; name?: string; id?: string }) =>
        (loc.label && String(loc.label).trim()) || (loc.name && String(loc.name).trim()) || String(loc.id || "");

    const handleAddLocation = (campaignId: string, currentList: LocationEntry[], name: string, type: "TARGET" | "EXCLUDE") => {
        if (!name.trim()) return;
        const newEntry: LocationEntry = {
            id: `loc_custom_${Date.now()}`,
            name: name.trim(),
            type,
        };
        onChange(campaignId, [...currentList, newEntry]);
        setSearchQuery(prev => ({ ...prev, [campaignId]: "" })); // clear search
    };

    const handleRemoveLocation = (campaignId: string, currentList: LocationEntry[], idToRemove: string) => {
        onChange(campaignId, currentList.filter(loc => loc.id !== idToRemove));
    };

    const handleTypeChange = (campaignId: string, currentList: LocationEntry[], idToModify: string, newType: "TARGET" | "EXCLUDE") => {
        onChange(campaignId, currentList.map(loc => loc.id === idToModify ? { ...loc, type: newType } : loc));
    };

    return (
        <div className={styles.locationTargetingFormContainer}>
            {locations.map((cp) => {
                const currentDataRaw = formData[cp.campaign_id] || cp.suggested_locations;
                const currentData = Array.isArray(currentDataRaw) ? currentDataRaw : [];
                const query = searchQuery[cp.campaign_id] || "";

                return (
                    <section key={cp.campaign_id} className={styles.campaignCard}>
                        <div className={styles.campaignName}>
                            {cp.campaign_name}
                        </div>

                        {cp.reason && (
                            <div className={styles.reasonBar}>
                                <strong>Aibex提案理由:</strong> {cp.reason}
                            </div>
                        )}

                        <div className={styles.suggestionBox}>
                            {/* Before - Currently configured locations */}
                            <div className={styles.beforePanel}>
                                <div className={styles.panelTitle}>現在の設定</div>
                                <div className={styles.locationList}>
                                    {cp.current_locations.length === 0 && (
                                        <div className={styles.emptyText}>設定なし</div>
                                    )}
                                    {cp.current_locations.map(loc => (
                                        <div key={loc.id} className={styles.locationItemBefore}>
                                            <span className={`${styles.typeBadge} ${loc.type === 'TARGET' ? styles.bgOn : styles.bgOff}`}>
                                                {loc.type === 'TARGET' ? '配信' : '除外'}
                                            </span>
                                            <span className={styles.locName}>{getLocationLabel(loc)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Arrow */}
                            <div className={styles.arrowIcon}>→</div>

                            {/* After - Dynamic editable list of locations */}
                            <div className={styles.afterPanel}>
                                <div className={styles.panelTitle}>AI提案</div>

                                {/* Search & Add Bar */}
                                <div className={styles.locationSearchRow}>
                                    <input
                                        type="text"
                                        placeholder="地域を検索（例: 東京都）"
                                        value={query}
                                        onChange={(e) => setSearchQuery(prev => ({ ...prev, [cp.campaign_id]: e.target.value }))}
                                        className={styles.textInputFull}
                                    />
                                    <div className={styles.addButtons}>
                                        <button
                                            type="button"
                                            className={`${styles.addBtn} ${styles.targetBtn}`}
                                            onClick={() => handleAddLocation(cp.campaign_id, currentData, query, "TARGET")}
                                            disabled={!query.trim()}
                                        >
                                            配信に追加
                                        </button>
                                        <button
                                            type="button"
                                            className={`${styles.addBtn} ${styles.excludeBtn}`}
                                            onClick={() => handleAddLocation(cp.campaign_id, currentData, query, "EXCLUDE")}
                                            disabled={!query.trim()}
                                        >
                                            除外に追加
                                        </button>
                                    </div>
                                </div>

                                {/* Active List */}
                                <div className={styles.locationListEditable}>
                                    {currentData.length === 0 && (
                                        <div className={styles.emptyText}>設定する地域を入力してください</div>
                                    )}
                                    {currentData.map(loc => (
                                        <div key={loc.id} className={styles.locationItemAfter}>
                                            <div className={styles.locItemMain}>
                                                <span className={styles.locName}>{getLocationLabel(loc)}</span>
                                                <div className={styles.radioGroupSmall}>
                                                    <label className={styles.radioLabel}>
                                                        <input
                                                            type="radio"
                                                            checked={loc.type === "TARGET"}
                                                            onChange={() => handleTypeChange(cp.campaign_id, currentData, loc.id, "TARGET")}
                                                        />
                                                        <span className={styles.radioText}>配信</span>
                                                    </label>
                                                    <label className={styles.radioLabel}>
                                                        <input
                                                            type="radio"
                                                            checked={loc.type === "EXCLUDE"}
                                                            onChange={() => handleTypeChange(cp.campaign_id, currentData, loc.id, "EXCLUDE")}
                                                        />
                                                        <span className={styles.radioText}>除外</span>
                                                    </label>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                className={styles.removeBtn}
                                                onClick={() => handleRemoveLocation(cp.campaign_id, currentData, loc.id)}
                                                title="削除"
                                            >
                                                <TrashIcon width={16} height={16} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>
                );
            })}
        </div>
    );
};

export default LocationTargetingForm;
